import { useEffect, useState } from "react";
import { ChevronDown, Trophy, Trash2, Zap, Moon, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useUnit } from "../context/UnitContext";

export default function HistoryPage() {
  const { unit, dw } = useUnit();
  const [sessions, setSessions] = useState([]);
  const [journal, setJournal] = useState({});

  const load = () => {
    api.get("/history").then(({ data }) => setSessions(data));
    api.get("/journal/all").then(({ data }) => {
      const map = {};
      data.forEach((j) => { map[`${j.date}-${j.bloc}-${j.jour}`] = j; });
      setJournal(map);
    }).catch(() => {});
  };
  const [open, setOpen] = useState(null);

  useEffect(() => { load(); }, []);

  const deleteSet = async (id) => {
    try {
      await api.delete(`/sets/${id}`);
      toast.success("Série supprimée");
      load();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const exportXlsx = async () => {
    try {
      const { data } = await api.get("/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gymtrack_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Export Excel téléchargé !");
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  return (
    <div className="pb-28">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/85 border-b border-[#2E2E3A] px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-4xl text-white leading-none">HISTORIQUE</h1>
            <p className="text-[#B0B0B4] text-xs mt-0.5">{sessions.length} séance{sessions.length > 1 ? "s" : ""} enregistrée{sessions.length > 1 ? "s" : ""}</p>
          </div>
          <button data-testid="export-excel-button" onClick={exportXlsx}
            className="h-10 px-3 border border-white/15 text-[#ccc] text-xs font-semibold flex items-center gap-1.5 hover:border-[#FF3B30] hover:text-white transition-colors">
            <FileDown size={15} /> Excel
          </button>
        </div>
      </header>
      <main className="px-4 mt-4 space-y-2">
        {sessions.length === 0 && (
          <p className="text-[#B0B0B4] text-sm text-center py-12">Aucune séance pour l'instant.</p>
        )}
        {sessions.map((s) => {
          const key = `${s.date}-${s.bloc}-${s.jour}`;
          const isOpen = open === key;
          return (
            <div key={key} className="bg-[#14141A] border border-[#2E2E3A]">
              <button data-testid={`session-${key}`} onClick={() => setOpen(isOpen ? null : key)}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors">
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{s.date} · Bloc {s.bloc} · {s.jour}</p>
                  <p className="text-[#B0B0B4] text-xs mt-0.5">
                    {s.nb_series} séries · {Math.round(dw(s.volume))} {unit} de volume
                    {s.nb_prs > 0 && <span className="text-amber-400"> · {s.nb_prs} record{s.nb_prs > 1 ? "s" : ""}</span>}
                  </p>
                </div>
                <ChevronDown size={18} className={`text-[#B0B0B4] transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-white/5 divide-y divide-white/5">
                  {journal[key] && (journal[key].energie || journal[key].sommeil || journal[key].notes) && (
                    <div data-testid={`journal-${key}`} className="px-4 py-2 bg-[#FF3B30]/5 text-xs space-y-0.5">
                      <div className="flex gap-4">
                        {journal[key].energie && <span className="text-[#ccc] flex items-center gap-1"><Zap size={11} className="text-[#FF3B30]" /> Énergie {journal[key].energie}/5</span>}
                        {journal[key].sommeil && <span className="text-[#ccc] flex items-center gap-1"><Moon size={11} className="text-[#FF3B30]" /> Sommeil {journal[key].sommeil}/5</span>}
                      </div>
                      {journal[key].notes && <p className="text-[#B0B0B4] italic">« {journal[key].notes} »</p>}
                    </div>
                  )}
                  {s.sets.map((st) => (
                    <div key={st.id} className="px-4 py-2 flex items-center gap-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="text-white truncate">{st.exercice} <span className="text-[#B0B0B4] text-xs">S{st.serie_num}</span></p>
                        {st.notes && <p className="text-[#B0B0B4] text-xs truncate">{st.notes}</p>}
                      </div>
                      <span className="text-[#ccc] font-mono text-xs shrink-0">{st.poids ? `${dw(st.poids)}${unit}` : "PDC"}×{st.reps}{st.rpe ? ` @${st.rpe}` : ""}</span>
                      {st.prs?.length > 0 && <Trophy size={14} className="text-amber-400 shrink-0" />}
                      <button data-testid={`delete-set-${st.id}`} onClick={() => deleteSet(st.id)}
                        className="text-[#555] hover:text-[#FF3B30] p-1.5 transition-colors shrink-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </main>
    </div>
  );
}
